/* eslint-disable quote-props */
/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpRequest, HttpErrorResponse, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, BehaviorSubject, throwError, from } from 'rxjs';
import { catchError, concatMap, switchMap, take, tap } from 'rxjs/operators';
import { ConfigService } from '../config.service';
import { LocalAuthService } from './local-auth.service';
import { BroadcastService } from '../services/broadcast.service';
import { LocalStorageService } from '../storage/local-storage.service';
import { TrackJsService } from '../services/track-js.service';

export class TokenRefreshError extends Error {
    constructor(message: string) {
        super(message);
        this.name = 'TokenRefreshError';
    }
}

@Injectable()
export class JwtInterceptor implements HttpInterceptor {
    private loggedIn: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);


   

    private refreshTokenSubject = new BehaviorSubject<null | { token: string, error: boolean }>(null);

    constructor(
        private router: Router,
        public auth: LocalAuthService,
        private configService: ConfigService,
        private broadcastService: BroadcastService,
        private localStorageService: LocalStorageService,
        private trackJs: TrackJsService
    ) {



        //another tab refreshed the token
        this.broadcastService.onMessage$.subscribe((message) => {
            if (message.type === 'refresh-user') {
                
                if (this.auth.isRefreshing) { 
                    if (message.data.status === 'complete') {
                        this.trackJs.consoleWarn('[Auth] Tab received refresh-user complete', { timestamp: new Date().toISOString() });
                        const token = this.auth.getAuthToken()
                        this.refreshTokenSubject.next({ token: token, error: false });
                        this.refreshTokenSubject.complete();
                        this.refreshTokenSubject = new BehaviorSubject<null | { token: string, error: boolean }>(null);
                       

                    }
                    else if (message.data.status === 'error') {
                        this.trackJs.consoleWarn('[Auth] Tab received refresh-user error', { timestamp: new Date().toISOString() });
                        this.refreshTokenSubject.next({ token: "", error: true });
                        this.refreshTokenSubject.complete();
                        this.refreshTokenSubject = new BehaviorSubject<null | { token: string, error: boolean }>(null);
                       
                    }
                }

            }
        });

        //our tab refreshed the token
        this.auth.tokenRefreshed$.subscribe(() => {
               this.refreshTokenSubject.next({ token: this.auth.getAuthToken(), error: false });
               this.refreshTokenSubject.complete();
               this.refreshTokenSubject = new BehaviorSubject<null | { token: string, error: boolean }>(null);
        })

    }

    private readonly fileUploadPaths = [
        /\/incidents\/\d*\/attachments/,
        /\/trip-journals\/trip\/\d*\/group-photo/,
        /\/trip-journals\/trip\/\d*\/entry\/\d*\/photo/,
        /\/ppm-365-updates\/\d*\/photo/,
        /\/staff\/\d*\/profile-photo/,
        /\/disasters\/\d*\/journal-entries\/\d*\/photo/,
        /\/disasters\/\d*\/photo/,
        /\/profiles-partners\/[^/]+\/photos/,
        /\/profiles-partners\/[^/]+\/gallery/,
        /\/profiles-partners\/contacts\/contact\/[^/]+\/photo/,
        /\/profiles-partners\/needs\/[^/]+\/[^/]+\/photo/,
        /\/profiles-partners\/events\/[^/]+\/[^/]+\/photo/,
        /\/profiles-partners\/[^/]+\/files/
    ]

    private readonly loginPaths = [
        /\/users\/refresh-login/,
        /\/users\/google-oauth/,
        /\/users\/reset-password/,
        /\/users\/forgot-password/
    ]

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        if (request.url.match(`${this.configService.dataApiUrl}`) && !this.loginPaths.some(path => request.url.match(path))) {
            const token = this.auth.getAuthToken();
            const clonedRequest = this.addTokenHeader(request, token);
            return next.handle(clonedRequest).pipe(catchError(error => {
                if (error instanceof HttpErrorResponse && !clonedRequest.url.includes("refresh") && error.status === 401) {
                    return this.handle401Error(clonedRequest, next);
                }

                if (error instanceof HttpErrorResponse && error.error && error.error.error && error.status !== 404) {
                    //This should be a loopback error
                    this.trackJs.consoleError("Loopback Error", { status: error.status, error: error.error.error });
                }
                return throwError((error));
            }));


        }
        else {
            return next.handle(request);
        }

    }

    private addTokenHeader(request: HttpRequest<any>, token: string) {
        //if (this.configService.staffApiActive) {
        let headers: any = {
            'Accept': 'application/json',
            'Authorization': `Bearer ${token}`,
            'mcapi-key': this.configService.mcapiKey
        }


        if (!this.fileUploadPaths.some(path => request.url.match(path)) || request.method !== 'POST') {
            headers = { ...headers, 'Content-Type': 'application/json; charset=utf-8' }
        }

        return request.clone({
            setHeaders: headers,
        });
        //}
        // else {
        //     return request.clone({
        //         setHeaders: {
        //             'Content-Type': 'application/json; charset=utf-8',
        //             'Accept': 'application/json',
        //             'Authorization': `Bearer ${token}`
        //         },
        //     });
        //}
    }

    private handle401Error(request: HttpRequest<any>, next: HttpHandler) {
        if (!this.auth.isRefreshing) {
          
            this.refreshTokenSubject.next(null);

            this.trackJs.consoleWarn('[Auth] Interceptor initiating refresh', { triggerUrl: request.url, timestamp: new Date().toISOString() });

            if (!this.auth.loggedIn) {
                
                return this.refreshFailure({message: "Not logged in.  Cannot refresh", reason: "not_logged_in"}, request.url);
            } else {

                
                return this.auth.refreshLogin().pipe(
                    switchMap((token) => {
                     

                      
                      
                        return next.handle(this.addTokenHeader(request, token.accessToken));
                    }),
                    catchError((err) => {
                        if (this.auth.isRefreshing) {
                           
                            return this.refreshFailure(err, request.url);
                        }
                        else {
                            return throwError(() => err)
                        }
                    })
                );
            }


        }
        else {
            this.trackJs.consoleWarn('[Auth] Interceptor waiting for refresh', { triggerUrl: request.url, timestamp: new Date().toISOString() });
            return this.waitForRefresh(request, next);
        }
    }

    private waitForRefresh(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {

        return this.refreshTokenSubject.pipe(switchMap((token) => {
            if (token && !token.error) {
                console.log("request refreshed continuing to handle  " + req.url)
                return next.handle(this.addTokenHeader(req, token.token));
            }
            else if (token && token.error) {
                console.log("request refresh failed " + req.url)
                return throwError(() => new TokenRefreshError("Token refresh failed"));
            }
            else {

                return this.waitForRefresh(req, next);
            }
        }));
    }

    private refreshFailure(err: unknown, triggerUrl?: string): Observable<never> {
        this.auth.isRefreshing = false;



        const reason = (typeof err === "object" && (err as { reason?: string })?.reason === "not_logged_in") ? "not_logged_in" : "refresh_failed";

        const logPayload: { reason: string; triggerUrl?: string; failureReason?: string; status?: number; message?: string } = {
            reason,
            triggerUrl
        };
        if (reason === 'not_logged_in') {
            logPayload.failureReason = this.auth.getLoggedInFailureReason() ?? 'unknown';
        } else if (reason === 'refresh_failed' && err && typeof err === 'object' && 'status' in err) {
            const httpErr = err as { status?: number; error?: { error?: { message?: string } }; message?: string };
            logPayload.status = httpErr.status;
            logPayload.message = httpErr?.error?.error?.message ?? httpErr?.message;
        }

        this.trackJs.consoleError('[Auth] Interceptor clearing token and redirecting to login', logPayload);

        this.auth.clearToken();

        this.refreshTokenSubject.next({ token: "", error: true })

        this.router.navigate(['/login'], { skipLocationChange: true, queryParams: { returnRoute: this.router.url } });


        return throwError(() => new TokenRefreshError("Token refresh failed"));
    }
}
